import { createElement, Fragment, type FC, type ReactNode, useMemo } from 'react';
import { cn, isBlankRichTextLine, isRichTextLayoutWhitespace } from '@/shared/lib';
import { sanitizeRichTextHtml } from '../RichTextEditor/quillHtml';

type Props = {
  /** Текст с разметкой */
  html: string;
  /**
   * Рендер текста между тегами; `offset` — позиция этого текста в тексте без разметки.
   * Без него текст рисуется как есть.
   */
  renderText?: (text: string, offset: number) => ReactNode;
  className?: string;
};

/**
 * Текст с разметкой: оформление отрисовывается, теги на экран не попадают.
 *
 * Разметку санитизирует `sanitizeRichTextHtml` — тем же набором тегов, что разрешает редактор,
 * поэтому заданное оформление не пропадает при чтении, а всё прочее убирается. Затем дерево
 * переносится в React-элементы: так текст между тегами остаётся во власти потребителя
 * (например, кликабельные маркеры), чего не даёт `dangerouslySetInnerHTML`.
 *
 * Границы строк, написанные переносами в самом тексте, сохраняются; отступы разметки между
 * блоками — не текст, и на экран не выносятся ({@link isRichTextLayoutWhitespace}). Строка,
 * оставленная пустой, своё место занимает: в разметке она записана пустым блоком
 * ({@link isBlankRichTextLine}).
 */
export const RichTextContent: FC<Props> = ({ html, renderText, className }) => {
  const elements = useMemo(() => {
    // Разметка позиции не занимает: потребителю нужен отсчёт по видимому тексту
    let textOffset = 0;

    const domNodeToReact = (node: ChildNode, index: number): ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        const offset = textOffset;
        // Пропуск разметки со счёта не снимаем: отсчёт потребителя ведётся по тексту целиком
        textOffset += text.length;
        if (isRichTextLayoutWhitespace(node as Text)) return null;
        return <Fragment key={index}>{renderText ? renderText(text, offset) : text}</Fragment>;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tag = element.tagName.toLowerCase();
        if (tag === 'br') return <br key={index} />;

        // Обход детей идёт и у пустого блока: им ведётся отсчёт текста для потребителя
        const children = Array.from(element.childNodes).map((child, childIndex) => domNodeToReact(child, childIndex));
        // Строку, оставленную пустой, разметка держит блоком без содержимого — высоты он не
        // занимает, и набранный отступ пропал бы. Перенос возвращает строке её место, как в правке.
        if (isBlankRichTextLine(element)) return createElement(tag, { key: index }, <br />);
        return createElement(tag, { key: index }, ...children);
      }

      return null;
    };

    const doc = new DOMParser().parseFromString(`<div>${sanitizeRichTextHtml(html)}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return null;

    return Array.from(root.childNodes).map((node, index) => domNodeToReact(node, index));
  }, [html, renderText]);

  return (
    <div
      className={cn(
        'whitespace-pre-line [&_ol]:list-decimal [&_ul]:list-disc [&_li]:ml-4 [&_ol]:ml-2 [&_ul]:ml-2',
        className,
      )}
    >
      {elements}
    </div>
  );
};
